
import { Dispatch } from 'react';
import { Action, ActionType } from './actions';
import { PropostaData, PropostaFinanceiraData, CondicaoPagamentoData, PropostaState } from './types';
import { propostaApi } from '@/services/api/proposta';


type AtualizarCampoFn = {
  (campo: 'proposta', payload: Partial<PropostaData>, viaUsuario?: boolean): void;
  (campo: 'financeira', payload: PropostaFinanceiraData | null, viaUsuario?: boolean): void;
  (campo: 'condicaoPagamento', payload: CondicaoPagamentoData | null, viaUsuario?: boolean): void;
};


export function criarAcoes(dispatch: Dispatch<Action>) {

  const atualizarCampo: AtualizarCampoFn = (campo: any, payload: any, viaUsuario = false) => {
    dispatch({
      type:
        campo === 'proposta'
          ? ActionType.SET_PROPOSTA
          : campo === 'financeira'
          ? ActionType.SET_FINANCEIRA
          : ActionType.SET_CONDICAO_PAGAMENTO,
      payload,
    });

    
    if (viaUsuario) {
      console.log('Campo atualizado:', campo, payload);
      dispatch({ type: ActionType.SET_PROPOSTA_SALVA, payload: false });
    }
  };  


  return {
    atualizarCampo,

    // ====== PROPOSTA ======
    async buscarPropostas(atendimentoId: string, propostaAtivaId?: number) {

      try {
        const propostas = await propostaApi.listarProposta(atendimentoId);
    
        if (propostas.length) {
          dispatch({ type: ActionType.SET_PROPOSTAS, payload: propostas });
    
          const ativa: PropostaData | null = propostaAtivaId
            ? propostas.find((p: PropostaData) => p.id === propostaAtivaId) || null
            : null;
    
          const selecionada = ativa;
    
          if (selecionada) {
            console.log('Proposta ativa encontrada:', selecionada);
            dispatch({ type: ActionType.SET_PROPOSTA, payload: selecionada });
          }
        }
      } catch (err) {
        console.error('Erro ao buscar propostas:', err);
      }
    },

    iniciarNovaProposta(atendimentoId: number) {
      dispatch({
        type: ActionType.SET_PROPOSTA,
        payload: {
          atendimento_id: atendimentoId,
          id: 0,
          resumo: '',
          descricao: '',
          listagem_itens: '',
          prazo_entrega: 0,
        }
      });

      dispatch({ type: ActionType.SET_FINANCEIRA, payload: null });
      dispatch({ type: ActionType.SET_CONDICAO_PAGAMENTO, payload: null });
    
      // Limpando erros antigos da proposta ao iniciar nova
      dispatch({
        type: ActionType.SET_ERROS_PROPOSTA,
        payload: {
          resumo: undefined,
          descricao: undefined,
          listagem_itens: undefined,
          prazo_entrega: undefined,
        }
      });
    },    


    async confirmarPagamento(propostaId: number, estado_pagamento: string, pagamentoTipo: string) {
      try {
        const pagamentoConfirmado = await propostaApi.atualizarEstadoPagamento(String(propostaId), {
          estado_pagamento: estado_pagamento,
          pagamentoTipo: pagamentoTipo,
        });
        dispatch({ type: ActionType.SET_PROPOSTA, payload: { estado_pagamento: estado_pagamento } });
      } catch (error) {
        throw new Error('Erro ao confirmar pagamento');
      }
    },

    async salvarProposta(state: PropostaState): Promise<number | null> {
      const { proposta, propostaFinanceira, condicaoPagamento, openFormPropostaFinanceira } = state;
      dispatch({ type: ActionType.SET_ENVIAR_IMAGEM, payload: false });
    
      // === Validação da proposta ===
      const errosProposta = validarCamposObrigatorios(proposta, state.imagens);
      if (Object.keys(errosProposta).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_PROPOSTA, payload: errosProposta });
        return null;
      }
    
      // === Atualiza ou cria proposta ===
      let propostaSalva;
      if (!proposta.id) {
        propostaSalva = await criarProposta(proposta);
        dispatch({ type: ActionType.SET_PROPOSTA, payload: propostaSalva });
      } else {
        propostaSalva = await atualizarProposta(proposta.id, proposta);
        dispatch({ type: ActionType.SET_PROPOSTA, payload: propostaSalva });
      }
      dispatch({ type: ActionType.SET_ERROS_PROPOSTA, payload: {} });
    
      // === Se o form de proposta financeira não estiver aberto, não valida/salva essas entidades ===
      dispatch({ type: ActionType.SET_ENVIAR_IMAGEM, payload: true });
      if (!openFormPropostaFinanceira && !propostaFinanceira?.id) {
        dispatch({ type: ActionType.SET_PROPOSTA_SALVA, payload: true });
        return propostaSalva.id ?? null;
      }
    
      // === Validação da proposta financeira ===
      const errosFinanceira = validarCamposPropostaFinanceira(propostaFinanceira!);
      if (Object.keys(errosFinanceira).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_FINANCEIRA, payload: errosFinanceira });
        return null;
      }      
    
      // === Criação/atualização da proposta financeira ===
      let propostaFinanceiraSalva: PropostaFinanceiraData;

      if (!propostaFinanceira?.id) {
        const response = await propostaApi.criarPropostaFinanceira({
          ...propostaFinanceira,
          valor_proposta: Number(propostaFinanceira?.valor_proposta ?? 0),
          desconto_em_porcentagem: Number(propostaFinanceira?.desconto_em_porcentagem ?? 0),
          valor_com_desconto: Number(propostaFinanceira?.valor_com_desconto ?? 0),
        });        
        propostaFinanceiraSalva = response.data;
      } else {
        propostaFinanceiraSalva = (
          await propostaApi.atualizarPropostaFinanceira(
            String(propostaFinanceira.id),
            {
              ...propostaFinanceira,
              valor_proposta: Number(propostaFinanceira?.valor_proposta ?? 0),
              desconto_em_porcentagem: Number(propostaFinanceira?.desconto_em_porcentagem ?? 0),
              valor_com_desconto: Number(propostaFinanceira?.valor_com_desconto ?? 0),
            }
          )
        ).data;        
      }


      await processarPropostaFinanceiraSalva(propostaSalva, propostaFinanceiraSalva);

      dispatch({ type: ActionType.SET_ERROS_FINANCEIRA, payload: {} });
    
      // === Validação da condição de pagamento ===
      const errosCondicao = validarCamposCondicaoPagamento(condicaoPagamento!);
      if (Object.keys(errosCondicao).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_CONDICAO_PAGAMENTO, payload: errosCondicao });
        return null;
      }
    
      // === Criação/atualização da condição de pagamento ===
      if (!condicaoPagamento?.id) {
        const novaCondicao = await propostaApi.criarCondicaoPagamento({
          ...condicaoPagamento,
          proposta_financeira_id: propostaFinanceiraSalva.id,
          valor_entrada: Number(condicaoPagamento?.valor_entrada ?? 0),
          quantidade_parcela: Number(condicaoPagamento?.quantidade_parcela ?? 0),
          desconto_entrada: Number(condicaoPagamento?.desconto_entrada ?? 0),
          valor_com_desconto_entrada: Number(condicaoPagamento?.valor_com_desconto_entrada ?? 0),
        })
        dispatch({ type: ActionType.SET_CONDICAO_PAGAMENTO, payload: novaCondicao });
      } else {
        const atualizada = await propostaApi.atualizarCondicaoPagamento(
          String(condicaoPagamento.id),
          {
            ...condicaoPagamento,
            proposta_financeira_id: propostaFinanceiraSalva.id,
            valor_entrada: Number(condicaoPagamento?.valor_entrada ?? 0),
            quantidade_parcela: Number(condicaoPagamento?.quantidade_parcela ?? 0),
            desconto_entrada: Number(condicaoPagamento?.desconto_entrada ?? 0),
            valor_com_desconto_entrada: Number(condicaoPagamento?.valor_com_desconto_entrada ?? 0),
          }
        );
        dispatch({ type: ActionType.SET_CONDICAO_PAGAMENTO, payload: atualizada });
      }
      if (propostaFinanceiraSalva.id) {
        await processarCondicaoPagamentoSalva(propostaFinanceiraSalva.id);
      }
      dispatch({ type: ActionType.SET_ERROS_CONDICAO_PAGAMENTO, payload: {} });
    
      dispatch({ type: ActionType.SET_ENVIAR_IMAGEM, payload: true });
      dispatch({ type: ActionType.SET_PROPOSTA_SALVA, payload: true });
      
      return propostaSalva.id ?? null;
    },    

    resetarProposta() {
      dispatch({ type: ActionType.RESET_ESTADO });
    },

    async deletarProposta(propostaId: number) {
      try {
        await propostaApi.deletarProposta(String(propostaId));
        dispatch({ type: ActionType.RESET_ESTADO });
      } catch (error) {
        console.error('Erro ao deletar proposta:', error);
      }
    },

    async enviarPropostaParaCliente(atendimento_id: string, data: { resumo: string; proposta_id: number }, state: PropostaState) {

      if (!state.propostaFinanceira || !state.propostaFinanceira.id || !state.condicaoPagamento || !state.condicaoPagamento.id) {
        throw new Error('Proposta financeira ou condição de pagamento não encontrada.');
      }
    
      const errosProposta = validarCamposObrigatorios(state.proposta, state.imagens);
      const errosFinanceira = validarCamposPropostaFinanceira(state.propostaFinanceira);
      const errosCondicao = validarCamposCondicaoPagamento(state.condicaoPagamento);
    
      const possuiErros =
        Object.keys(errosProposta).length > 0 ||
        Object.keys(errosFinanceira).length > 0 ||
        Object.keys(errosCondicao).length > 0;
    
      if (Object.keys(errosProposta).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_PROPOSTA, payload: errosProposta });
      }
    
      if (Object.keys(errosFinanceira).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_FINANCEIRA, payload: errosFinanceira });
      }
    
      if (Object.keys(errosCondicao).length > 0) {
        dispatch({ type: ActionType.SET_ERROS_CONDICAO_PAGAMENTO, payload: errosCondicao });
      }
    
      if (possuiErros) {
        throw new Error('Existem campos obrigatórios não preenchidos.');
      }
    
      const response = await propostaApi.enviarPropostaParaCliente(atendimento_id, data);
      if (response.status === 200) {
        return true;
      } else {
        throw new Error('Erro ao enviar proposta para o cliente');
      }
    },




  

    // ======================= PROPOSTA FINANCEIRA ===========================
  
    async buscarPropostaFinanceira(propostaFinanceiraId: number) {
      try {
        const response = await propostaApi.buscarPropostaFinanceira(String(propostaFinanceiraId));
        console.log('Proposta financeira encontrada:', response);
        dispatch({ type: ActionType.SET_FINANCEIRA, payload: response });
      } catch (error) {
        console.error('Erro ao buscar proposta financeira ativa:', error);
      }
    },

    async listarPropostasFinanceiras(propostaId: number) {
      try {
        const response = await propostaApi.listarPropostaFinanceira(String(propostaId));
        console.log('Lista - propostas financeiras encontradas:', response.data);
        dispatch({ type: ActionType.SET_PROPOSTAS_FINANCEIRAS, payload: response.data });
      } catch (error) {
        console.error('Erro ao listar propostas financeiras:', error);
      }
    },

    async deletarPropostaFinanceira(propostaFinanceiraid: number, propostaId: number) {
      try {
        await propostaApi.deletarPropostaFinanceira(String(propostaFinanceiraid));
        await listarPropostasFinanceiras(propostaId);
      } catch (error) {
        console.error('Erro ao deletar proposta financeira:', error);
      }
    },


    async buscarCondicaoPagamento(propostaFinanceiraId: string) {
      try {
        const response = await propostaApi.buscarPorCondicaoPagamento(propostaFinanceiraId);
        dispatch({ type: ActionType.SET_CONDICAO_PAGAMENTO, payload: response });
      } catch (error) {
        console.error('Erro ao buscar condição de pagamento:', error);
      }
    },

  };

  



  // ================= Funções para Proposta Financeira ===================

  async function processarPropostaFinanceiraSalva(propostaSalva: PropostaData, propostaFinanceiraSalva: PropostaFinanceiraData) {
    dispatch({ type: ActionType.SET_FINANCEIRA, payload: propostaFinanceiraSalva });
  
    dispatch({
      type: ActionType.SET_PROPOSTA,
      payload: {
        ...propostaSalva,
        PropostaFinanceiraAtivaId: propostaFinanceiraSalva.id,
      }
    });
    if (propostaSalva.id) {
      await listarPropostasFinanceiras(propostaSalva.id);
    }
  }

  async function listarPropostasFinanceiras(propostaId: number) {
    try {
      const response = await propostaApi.listarPropostaFinanceira(String(propostaId));
      dispatch({ type: ActionType.SET_PROPOSTAS_FINANCEIRAS, payload: response.data });
    } catch (error) {
      console.error('Erro ao listar propostas financeiras:', error);
    }
  }

  async function processarCondicaoPagamentoSalva(propostaFinanceiraId: number) {
    const response = await propostaApi.buscarPorCondicaoPagamento(String(propostaFinanceiraId));
    dispatch({ type: ActionType.SET_CONDICAO_PAGAMENTO, payload: response });
  }
  


}


// ================ Funções para Proposta ===================

async function criarProposta(proposta: PropostaData): Promise<PropostaData> {
  try {
    const response = await propostaApi.criarProposta(proposta);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar proposta:', error);
    throw new Error('Erro ao criar proposta');
  }
}


async function atualizarProposta(id: number, proposta: Partial<PropostaData>): Promise<PropostaData> {
  try {
    const response = await propostaApi.atualizarProposta(String(id), proposta);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar proposta:', error);
    throw new Error('Erro ao atualizar proposta');
  }
}


function stripHtmlTags(html: string = ''): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

function validarCamposObrigatorios(
  proposta: PropostaData,
  imagens: string[]
): Partial<PropostaState['errosProposta']> {
  const erros: Partial<PropostaState['errosProposta']> = {};

  const descricaoTexto = stripHtmlTags(proposta.descricao);
  const listagemTexto = stripHtmlTags(proposta.listagem_itens);

  if (!proposta.resumo?.trim()) erros.resumo = 'Resumo é obrigatório';
  else if (proposta.resumo.length > 200) erros.resumo = 'Resumo deve ter no máximo 200 caracteres';

  if (!descricaoTexto) erros.descricao = 'Descrição é obrigatória';
  else if (descricaoTexto.length > 6000) erros.descricao = 'Descrição muito longa';

  if (!listagemTexto) erros.listagem_itens = 'Listagem é obrigatória';
  else if (listagemTexto.length > 3500) erros.listagem_itens = 'Listagem muito longa';

  if (!proposta.prazo_entrega || proposta.prazo_entrega < 1 || proposta.prazo_entrega > 60)
    erros.prazo_entrega = 'Prazo deve ser entre 1 e 60 dias';

  if (!imagens || imagens.length === 0)
    erros.imagens = 'Adicione pelo menos uma imagem.';

  return erros;
}


function validarCamposPropostaFinanceira(financeira: PropostaFinanceiraData): Partial<PropostaState['errosPropostaFinanceira']> {
  const erros: Partial<PropostaState['errosPropostaFinanceira']> = {};

  if (!financeira?.valor_proposta || isNaN(Number(financeira.valor_proposta))) {
    erros.valor_proposta = 'Valor da proposta é obrigatório e deve ser numérico.';
  }

  if (financeira?.desconto_em_porcentagem && isNaN(Number(financeira.desconto_em_porcentagem))) {
    erros.desconto_em_porcentagem = 'Desconto deve ser numérico.';
  }

  return erros;
}

function validarCamposCondicaoPagamento(condicao: CondicaoPagamentoData): Partial<PropostaState['errosCondicaoPagamento']> {
  const erros: Partial<PropostaState['errosCondicaoPagamento']> = {};

  if (condicao.tipo_pagamento === 'ENTRADA_PARCELADO' && (!condicao.valor_entrada || isNaN(Number(condicao.valor_entrada)))) {
    erros.valor_entrada = 'Valor de entrada é obrigatório e deve ser numérico.';
  }

  if (!condicao.quantidade_parcela || condicao.quantidade_parcela <= 0) {
    erros.quantidade_parcela = 'Quantidade de parcelas deve ser maior que zero.';
  }

  return erros;
}



